import axios, { AxiosResponse } from 'axios';
import { get } from 'lodash-es';
import { BusiCode, getHttpMsg, promptMsg, API_PREFIX } from '@/infrastructure/axios/code';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { logger } from '@/infrastructure/logger';
import { ChangeUserRequest } from '@/service/auth';

export const PLATFORM = 'bugly';

const HEALTH_CHECK = `https://t.internal.bugly.qq.com/${API_PREFIX}/health/check`;

const TENCENT_CLOUD_PRELOGIN = 'https://cloud.tencent.com/login/saas/prelogin';

const TENCENT_CLOUD_REGISTER = 'https://cloud.tencent.com/login/saas/register';

const TENCENT_CLOUD_QQ_CALLBACK = 'https://cloud.tencent.com/login/qqAccessCallback';

// 腾讯云注册接口码表
// https://tapd.woa.com/QCloud_2015/markdown_wikis/show/#1210103951000687699?code=TOF4TeyJrZXkiOiJEU2lpMFF3aVRZd2NnbVY1YWdLRHNtTG02NVppeEV6SyIsImlzcyI6IjEwLjk5LjIwOC42MSIsImhrIjoiIiwiYXVkIjoidGVnX3RhcGRfYXV0aCIsImhhc2giOiI0QTJFOTBEMjQyMDZBMzYyNTI2MzRFRTNEODg2Qzc0OUM5MzRFNUYzMTZBNTZFMDdCQjQ1NUQ2OTdDMzZBRkQwIiwibmgiOiI5RTVEMkE5MDY5NzE0MTQ2RjM0MTRGNkYzODM2ODk5MTg0M0Y3RUU5MUI1MzZFM0FFRDU4NzdBQzQzOEM1RTBEIn0
const ERROR_MSG: Record<number, string> = {
  9003: '参数错误',
  12103: '手机找不到验证码',
  12104: '手机验证码过期',
  12105: '手机验证码输入错误达到上限',
  12106: '手机验证码错误',
  12107: '邮箱找不到验证码',
  12108: '邮箱验证码过期',
  12109: '邮箱验证码输入错误达到上限',
  12110: '邮箱验证码错误',
  10003: '内部错误，网络请求异常',
  70009: '手机超过绑定上限',
  12111: '账号已存在',
};

export enum LoginType {
  IOA = 'ioa',
  TencentCloud = 'tencent_cloud',
  DjiOauth = 'dji_oauth_bugly'
}

export enum TencentCloudLoginType {
  Wechat = 'wechat',
  QQ = 'qq'
}

export enum SubSystemType {
  SubAccount = 'SubAccount',
}

export type TencentCloudAccountOwnerInfo = {
  account: string;
  checkStatus: string | null;
  displayName: string;
  email: string;
  emailFlag: number;
  isDefaultOwner: boolean;
  phone: string;
  phoneFlag: number;
  subSystemType: SubSystemType;
  subUserName: string;
  uin: string;
  userName: string;
  extAttr: {
    needResetStoken: boolean;
    needResetToken: boolean;
  }
};

export type TencentCloudAccount = {
  uin: string;
  ownerInfo: TencentCloudAccountOwnerInfo[]
};

export type TencentCloudToken = {
  expireTimestamp: number;
  key: string;
  ownerUin: string;
  sid: string;
  uin: string;
};


/**
 * 回包拦截器
 * @param response
 */
const onResFulfilled = (response: AxiosResponse) => {
  const { data } = response;

  const code = data?.code ?? null;
  if (code !== BusiCode.Ok) {
    logger.info('Axios|onResFulfilled', code, response.config.url);
    const msg = ERROR_MSG[code] || '网络异常，请稍后再试';
    promptMsg(msg, code, response.config.url);
    return Promise.reject(new Error(code));
  }

  return Promise.resolve(data);
};

export const onResRejected = (error: any) => {
  const code = error?.response?.status;
  logger.error('Axios|onResRejected:', code);
  const msg = getHttpMsg(code);
  promptMsg(msg, code, error?.response?.config.url);

  return Promise.reject(new Error(code));
};

export interface LoginForWechatResponse {
  token?: {
    expireTimeStamp: number;
    key: string;
    ownerUin: string;
    uin: string;
  },
  accountList: TencentCloudAccount[]
}

export interface SaasRegisterRequest {
  phoneNumber: string;
  phoneVerifyCode: string;
  registerType: string;
  token: string;
}

export enum RegisterType {
  Wechat = 'wechat',
  QQ = 'qq'
}

export class TencentCloudService extends AbstractHttpClient {
  constructor() {
    super({
      merged: false,
      axiosConfig: {
        withCredentials: true,
      },
      interceptors: {
        response: {
          onFulfilled: onResFulfilled,
          onRejected: onResRejected,
        },
      },
    });
  }

  public async preloginForWechat(code: string): Promise<LoginForWechatResponse> {
    try {
      const res = await this.client.post(TENCENT_CLOUD_PRELOGIN, {
        clientUA: navigator.userAgent,
        platform: PLATFORM,
        type: 'wx',
        action: 'verify',
        areaType: 1,
        reportLoginInfo: true,
        data: {
          code,
          type: 1,
        },
        detail: {
          loginType: 'wx',
          platform: PLATFORM,
          clientType: 'pcweb',
        },
      });

      return {
        token: get(res, 'data.token', undefined),
        accountList: get(res, 'data.accountList', []),
      };
    } catch (e) {
      return {
        token: undefined,
        accountList: [],
      };
    }
  };

  public async preloginForQQ(code: string) {
    try {
      const res = await this.client.post(TENCENT_CLOUD_PRELOGIN, {
        clientUA: navigator.userAgent,
        platform: PLATFORM,
        type: 'qq',
        action: 'verify',
        areaType: 1,
        reportLoginInfo: true,
        data: {
          code,
          redirect_uri: TENCENT_CLOUD_QQ_CALLBACK,
        },
        detail: {
          loginType: 'qq',
          platform: PLATFORM,
          clientType: 'pcweb',
        },
      }, {
        withCredentials: true,
      });

      return {
        token: get(res, 'data.token', undefined),
        accountList: get(res, 'data.accountList', []),
      };
    } catch (e) {
      return {
        token: undefined,
        accountList: [],
      };
    }
  };

  public async saasLogin(params: SaasRegisterRequest): Promise<TencentCloudToken | null> {
    try {
      const res = await this.client.post(TENCENT_CLOUD_REGISTER, {
        clientUA: navigator.userAgent,
        platform: PLATFORM,
        countryName: 'CN',
        countryCode: '86',
        lang: 'zh',
        // 中国版
        area: 1,
        ...params,
      });

      return get(res, 'data', null) as TencentCloudToken;
    } catch (e) {
      return null;
    }
  };

  public async pinOA() {
    try {
      await axios.head(HEALTH_CHECK, {
        withCredentials: false,
      });
      return true;
    } catch (e) {
      return false;
    }
  };
}

export class TencentCloudBFFService extends AbstractHttpClient {
  constructor() {
    super({});
  }

  public async change(changeParams: ChangeUserRequest) {
    try {
      const res = await this.client.post(`/${API_PREFIX}/api/login/change-token-user`, changeParams);
      return get(res, 'data.data');
    } catch (e) {
      return null;
    }
  }
}

export const tencentCloudService = new TencentCloudService();

export const tencentCloudBFFService = new TencentCloudBFFService();
